import React, { memo, useMemo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import type { OrgNodeData } from './orgChartUtils';

import { tokens } from '@design';
const nodeStyles: Record<string, React.CSSProperties> = {
  base: {
    padding: '10px 16px',
    borderRadius: tokens.radii.absolute["radius-8"],
    minWidth: 160,
    maxWidth: 200,
    textAlign: 'center',
    cursor: 'pointer',
    fontFamily: 'JF-Flat-Regular, system-ui, sans-serif',
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    position: 'relative',
    transition: 'box-shadow 0.2s',
  },
};

function OrgChartNodeComponent({ data }: NodeProps) {
  const nodeData = data as unknown as OrgNodeData;
  const {
    treeNode,
    label,
    level,
    isExpanded,
    childrenCount,
    primaryColor,
    secondaryColor,
    onToggleExpand,
    onNodeClick,
  } = nodeData;

  const isSyntheticRoot = treeNode.id === -1;
  const goalsCount = treeNode.strategic_goals?.length ?? 0;

  // Level-based styling
  let style: React.CSSProperties;
  if (isSyntheticRoot || level === 0) {
    // Root / org name
    style = {
      ...nodeStyles.base,
      backgroundColor: secondaryColor,
      color: tokens.colors.raw.white,
      fontFamily: 'JF-Flat-Medium, system-ui, sans-serif',
      fontSize: tokens.typography.paragraph["paragraph small font size"],
      fontWeight: 600,
    };
  } else if (level === 1) {
    // Department level
    style = {
      ...nodeStyles.base,
      backgroundColor: primaryColor,
      color: tokens.colors.raw.white,
      fontFamily: 'JF-Flat-Medium, system-ui, sans-serif',
    };
  } else if (level === 2) {
    // Section level
    style = {
      ...nodeStyles.base,
      backgroundColor: '#EBF5FF',
      border: `1.5px solid ${primaryColor}`,
      color: '#1E3A5F',
    };
  } else {
    // Subsidiary level
    style = {
      ...nodeStyles.base,
      backgroundColor: tokens.colors.raw.gray["50"],
      border: '1.5px dashed #D1D5DB',
      color: tokens.colors.raw.gray["500"],
      fontSize: tokens.typography.paragraph["paragraph mini font size"],
    };
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isSyntheticRoot) {
      onNodeClick(treeNode);
    }
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleExpand(treeNode.id);
  };

  return (
    <div
      style={style}
      onClick={handleClick}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
      }}
    >
      {/* Target handle (top) — hidden for root */}
      {!isSyntheticRoot && (
        <Handle
          type="target"
          position={Position.Top}
          style={{ opacity: 0, width: 8, height: 8 }}
        />
      )}

      {/* Node label */}
      <div style={{ marginBottom: childrenCount > 0 || goalsCount > 0 ? 4 : 0 }}>
        {label}
      </div>

      {/* Goals badge */}
      {goalsCount > 0 && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.spacing.absolute["1"],
            backgroundColor: 'rgba(139, 92, 246, 0.15)',
            color: '#7C3AED',
            borderRadius: tokens.radii.absolute["radius-10"],
            padding: '2px 8px',
            fontSize: tokens.typography.paragraph["paragraph caption font size"],
          }}
        >
          <span style={{ fontSize: 10 }}>🎯</span>
          {goalsCount}
        </div>
      )}

      {/* Expand/collapse toggle */}
      {childrenCount > 0 && (
        <div
          onClick={handleToggle}
          style={{
            position: 'absolute',
            bottom: -12,
            insetInlineStart: '50%',
            transform: 'translateX(-50%)',
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: tokens.colors.raw.white,
            border: '1.5px solid #D1D5DB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: 10,
            color: tokens.colors.raw.gray["500"],
            zIndex: 10,
          }}
        >
          {isExpanded ? '▲' : '▼'}
        </div>
      )}

      {/* Source handle (bottom) */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ opacity: 0, width: 8, height: 8 }}
      />
    </div>
  );
}

export default memo(OrgChartNodeComponent);
